/**
 * Peppol settings + transmissions client using tRPC.
 *
 * Mirrors the apiKeyClient pattern: a thin hook around the tRPC
 * namespace that the frontend-plugin-peppol pages consume.
 */

import type {
  ActivatePeppolInput,
  PeppolEnvironment,
  PeppolRegistrationResult,
  PeppolSettings,
  PeppolTransmission,
  SettingsFormValues,
} from '@xhubio-saas/frontend-plugin-peppol'
import { useCallback } from 'react'
import { trpc } from '@/trpc'

/**
 * Returns an upsert callback for the Peppol SettingsForm. The form
 * surfaces a `SettingsFormValues` payload; we pass it straight to
 * the tRPC `peppol.settings.upsert` mutation.
 */
export function usePeppolSaveSettings(): (values: SettingsFormValues) => Promise<void> {
  // biome-ignore lint/suspicious/noExplicitAny: tRPC typing roundtrips through any in this app
  const mutation = (trpc as any).peppol.settings.upsert.useMutation()
  return useCallback(
    async (values: SettingsFormValues) => {
      await mutation.mutateAsync(values)
    },
    [mutation]
  )
}

/**
 * Returns the cached Peppol settings for the given environment.
 * Returns `null` when the org has not yet configured the scope.
 */
export function usePeppolSettings(environment: PeppolEnvironment): {
  data: PeppolSettings | null | undefined
  isLoading: boolean
} {
  // biome-ignore lint/suspicious/noExplicitAny: tRPC typing roundtrips through any in this app
  const query = (trpc as any).peppol.settings.get.useQuery({ environment })
  return { data: query.data ?? null, isLoading: query.isLoading }
}

/**
 * Returns the most-recent transmissions for the active org.
 */
export function usePeppolTransmissions(): {
  rows: PeppolTransmission[]
  isLoading: boolean
} {
  // biome-ignore lint/suspicious/noExplicitAny: tRPC typing roundtrips through any in this app
  const query = (trpc as any).peppol.transmissions.list.useQuery({ limit: 200 })
  return { rows: query.data ?? [], isLoading: query.isLoading }
}

/**
 * Registration lifecycle for the customer-facing ActivationPanel:
 * status query + activate/deactivate mutations (peppol.registration.*).
 * Mutations refetch the status so the badge updates immediately.
 */
export function usePeppolRegistration(environment: PeppolEnvironment): {
  status: PeppolRegistrationResult | undefined
  isLoading: boolean
  activate: (input: Omit<ActivatePeppolInput, 'environment'>) => Promise<void>
  deactivate: () => Promise<void>
} {
  // biome-ignore lint/suspicious/noExplicitAny: tRPC typing roundtrips through any in this app
  const query = (trpc as any).peppol.registration.status.useQuery({ environment })
  // biome-ignore lint/suspicious/noExplicitAny: tRPC typing roundtrips through any in this app
  const activateMutation = (trpc as any).peppol.registration.activate.useMutation()
  // biome-ignore lint/suspicious/noExplicitAny: tRPC typing roundtrips through any in this app
  const deactivateMutation = (trpc as any).peppol.registration.deactivate.useMutation()

  const activate = useCallback(
    async (input: Omit<ActivatePeppolInput, 'environment'>) => {
      await activateMutation.mutateAsync({ ...input, environment })
      await query.refetch()
    },
    [activateMutation, environment, query]
  )
  const deactivate = useCallback(async () => {
    await deactivateMutation.mutateAsync({ environment })
    await query.refetch()
  }, [deactivateMutation, environment, query])

  return {
    status: query.data,
    isLoading: query.isLoading || activateMutation.isPending || deactivateMutation.isPending,
    activate,
    deactivate,
  }
}
